import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { videoAPI } from '../../utils/api';
import i18n from '../../utils/i18n';

export const fetchVideo = createAsyncThunk(
  'video/fetchVideo',
  async (videoId, { rejectWithValue }) => {
    try {
      // Получаем текущий язык для инвалидации кеша
      const currentLang = i18n.language || 'en';
      const response = await videoAPI.getVideo(videoId, currentLang);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch video');
    }
  }
);

export const fetchRouteVideos = createAsyncThunk(
  'video/fetchRouteVideos',
  async (routeId, { rejectWithValue }) => {
    try {
      // Получаем текущий язык для инвалидации кеша
      const currentLang = i18n.language || 'en';
      const response = await videoAPI.getVideosByRoute(routeId, currentLang);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch route videos');
    }
  }
);

export const likeVideo = createAsyncThunk(
  'video/likeVideo',
  async (videoId, { rejectWithValue }) => {
    try {
      const response = await videoAPI.likeVideo(videoId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to like video');
    }
  }
);

export const createVideo = createAsyncThunk(
  'video/createVideo',
  async (videoData, { rejectWithValue }) => {
    try {
      const response = await videoAPI.createVideo(videoData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create video');
    }
  }
);

const videoSlice = createSlice({
  name: 'video',
  initialState: {
    currentVideo: null,
    routeVideos: [],
    userVideos: [],
    isLoading: false,
    error: null,
  },
  reducers: {
    clearCurrentVideo: (state) => {
      state.currentVideo = null;
    },
    clearRouteVideos: (state) => {
      state.routeVideos = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    setCurrentVideoHasFaces: (state, action) => {
      const { videoId, hasFaces } = action.payload;
      if (state.currentVideo && state.currentVideo.id === videoId) {
        state.currentVideo.has_faces = hasFaces;
        state.currentVideo.hasFaces = hasFaces;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch video
      .addCase(fetchVideo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVideo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentVideo = action.payload;
      })
      .addCase(fetchVideo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        // При ошибке (включая 404) очищаем currentVideo
        state.currentVideo = null;
      })
      // Fetch route videos
      .addCase(fetchRouteVideos.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRouteVideos.fulfilled, (state, action) => {
        state.isLoading = false;
        state.routeVideos = action.payload;
      })
      .addCase(fetchRouteVideos.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Like video
      .addCase(likeVideo.fulfilled, (state, action) => {
        if (state.currentVideo && state.currentVideo.id === action.meta.arg) {
          state.currentVideo.likes += 1;
        }
      })
      // Create video
      .addCase(createVideo.fulfilled, (state, action) => {
        state.routeVideos.unshift(action.payload);
      });
  },
});

export const { clearCurrentVideo, clearRouteVideos, clearError, setCurrentVideoHasFaces } = videoSlice.actions;
export default videoSlice.reducer;